import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Button,
  Select, MenuItem, FormControl, makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { CPP_DATA, resolveCppBatch } from './cppStaticData';
import { getRecipeInfo } from './recipeStructureData';

// ─── Chart geometry ────────────────────────────────────────────────────────────
const G = { svgW: 720, svgH: 320, ml: 56, mrBase: 16, mrOverlay: 56, mt: 18, mb: 52 };
G.plotH = G.svgH - G.mt - G.mb;

const niceStep = (span, target = 6) => {
  const raw = (span || 1) / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = norm >= 5 ? 5 : norm >= 2 ? 2 : 1;
  return step * mag;
};

// Compute a nice [min,max]+ticks for a set of values/guides
const scaleFor = (values, guides = []) => {
  const all = [...values, ...guides].filter((v) => v != null && !isNaN(v));
  if (!all.length) return { min: 0, max: 1, ticks: [0, 1] };
  let lo = Math.min(...all), hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.1 || 1;
  lo -= pad; hi += pad;
  const step = niceStep(hi - lo);
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toFixed(6)));
  return { min: lo, max: hi, ticks };
};

const OVERLAY_COLOR = '#E8772E';
const PRIMARY_COLOR = '#3D6BE5';

/**
 * SVG chart for a primary CPP series with an optional overlay (2nd parameter, right axis).
 * series/overlay = { title, unit, setPoint, rangeLow, rangeHigh, points: [[h, v, tsLabel, sp], ...] }
 */
const CppTimeSeriesChart = ({ series, overlay = null, xAxis = 'timestamp', chartType = 'line', svgRef }) => {
  const pts = series.points || [];
  const { rangeLow, rangeHigh, unit } = series;
  const isVolume = series.xType === 'volume'; // chromatogram: X is Volume (mL), not time
  const mr = overlay ? G.mrOverlay : G.mrBase;
  const plotW = G.svgW - G.ml - mr;

  const pScale = useMemo(
    () => scaleFor(pts.map((p) => p[1]), [rangeLow, rangeHigh, ...pts.map((p) => p[3])]),
    [pts, rangeLow, rangeHigh]
  );
  const oPts = overlay ? overlay.points || [] : [];
  const oScale = useMemo(() => (overlay ? scaleFor(oPts.map((p) => p[1])) : null), [overlay, oPts]);

  const hMin = pts.length ? pts[0][0] : 0;
  const hMax = pts.length ? pts[pts.length - 1][0] : 1;
  const tx = (h) => G.ml + ((h - hMin) / (hMax - hMin || 1)) * plotW;
  const ty = (v) => G.mt + ((pScale.max - v) / (pScale.max - pScale.min || 1)) * G.plotH;
  const tyO = (v) => G.mt + ((oScale.max - v) / (oScale.max - oScale.min || 1)) * G.plotH;

  const xTickCount = Math.min(9, pts.length);
  const xTicks = [];
  for (let i = 0; i < xTickCount; i++) {
    const idx = Math.round((i / (xTickCount - 1 || 1)) * (pts.length - 1));
    const p = pts[idx];
    if (p) xTicks.push({ x: tx(p[0]), label: isVolume ? p[2] : (xAxis === 'hours' ? `${p[0]}h` : p[2]) });
  }

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ');
  const overlayPath = overlay
    ? oPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${tx(p[0]).toFixed(1)},${tyO(p[1]).toFixed(1)}`).join(' ')
    : '';
  const setPath = pts.filter((p) => p[3] != null)
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${tx(p[0]).toFixed(1)},${ty(p[3]).toFixed(1)}`).join(' ');
  const barW = Math.max(1, (plotW / (pts.length || 1)) * 0.6);

  return (
    <svg ref={svgRef} viewBox={`0 0 ${G.svgW} ${G.svgH}`} style={{ width: '100%', minWidth: 420, display: 'block' }}>
      {/* Primary grid + left Y labels */}
      {pScale.ticks.map((t) => (
        <g key={t}>
          <line x1={G.ml} x2={G.ml + plotW} y1={ty(t)} y2={ty(t)} stroke="#ECECEC" strokeWidth={0.5} />
          <text x={G.ml - 8} y={ty(t) + 3} textAnchor="end" fontSize={9} fill="#666">{t}</text>
        </g>
      ))}
      {/* Left Y title */}
      <text x={14} y={G.mt + G.plotH / 2} textAnchor="middle" fontSize={10} fill="#555"
        transform={`rotate(-90,14,${G.mt + G.plotH / 2})`}>
        {series.title.split(' - ')[0]}{unit ? ` (${unit})` : ''}
      </text>

      {/* Right Y axis (overlay) */}
      {overlay && oScale.ticks.map((t) => (
        <text key={'o' + t} x={G.ml + plotW + 8} y={tyO(t) + 3} textAnchor="start" fontSize={9} fill={OVERLAY_COLOR}>{t}</text>
      ))}
      {overlay && (
        <text x={G.svgW - 8} y={G.mt + G.plotH / 2} textAnchor="middle" fontSize={10} fill={OVERLAY_COLOR}
          transform={`rotate(90,${G.svgW - 8},${G.mt + G.plotH / 2})`}>
          {overlay.title.split(' - ')[0]}{overlay.unit ? ` (${overlay.unit})` : ''}
        </text>
      )}

      {/* X labels */}
      {xTicks.map((t, i) => (
        <text key={i} x={t.x} y={G.mt + G.plotH + 16} textAnchor="middle" fontSize={8} fill="#666">{t.label}</text>
      ))}
      <text x={G.ml + plotW / 2} y={G.svgH - 6} textAnchor="middle" fontSize={10} fill="#555">
        {isVolume ? (series.xTitle || 'Volume (mL)') : (xAxis === 'hours' ? 'Elapsed Hours' : 'Timestamp')}
      </text>

      {/* Axes */}
      <line x1={G.ml} y1={G.mt} x2={G.ml} y2={G.mt + G.plotH} stroke="#999" strokeWidth={1} />
      <line x1={G.ml} y1={G.mt + G.plotH} x2={G.ml + plotW} y2={G.mt + G.plotH} stroke="#999" strokeWidth={1} />
      {overlay && <line x1={G.ml + plotW} y1={G.mt} x2={G.ml + plotW} y2={G.mt + G.plotH} stroke={OVERLAY_COLOR} strokeWidth={0.75} />}

      {/* Range + set-point (primary only) */}
      {rangeHigh != null && <line x1={G.ml} x2={G.ml + plotW} y1={ty(rangeHigh)} y2={ty(rangeHigh)} stroke="#D32F2F" strokeWidth={1.25} strokeDasharray="6,4" />}
      {rangeLow != null && <line x1={G.ml} x2={G.ml + plotW} y1={ty(rangeLow)} y2={ty(rangeLow)} stroke="#D32F2F" strokeWidth={1.25} strokeDasharray="6,4" />}
      {setPath && <path d={setPath} fill="none" stroke="#2E7D32" strokeWidth={1.4} strokeDasharray="5,3" strokeLinejoin="round" />}

      {/* Primary data */}
      {chartType === 'bar' ? (
        pts.map((p, i) => {
          const y = ty(p[1]); const h = G.mt + G.plotH - y;
          const out = (rangeHigh != null && p[1] > rangeHigh) || (rangeLow != null && p[1] < rangeLow);
          return <rect key={i} x={tx(p[0]) - barW / 2} y={y} width={barW} height={Math.max(0, h)} fill={out ? '#D32F2F' : PRIMARY_COLOR} opacity={0.9} />;
        })
      ) : (
        <path d={linePath} fill="none" stroke={PRIMARY_COLOR} strokeWidth={1.6} strokeLinejoin="round" />
      )}

      {/* Overlay line */}
      {overlay && overlayPath && <path d={overlayPath} fill="none" stroke={OVERLAY_COLOR} strokeWidth={1.6} strokeLinejoin="round" />}
    </svg>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  paper: { borderRadius: 12, maxWidth: 940 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '16px 16px 4px' },
  title: { fontWeight: 700, fontSize: '1.25rem' },
  closeWrap: { position: 'absolute', right: 12, top: 10, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' },
  closeLabel: { fontWeight: 600, fontSize: '0.85rem' },
  selectorRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16, padding: '8px 24px 12px' },
  fieldLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#333', marginBottom: 4 },
  select: { minWidth: 150, fontSize: '0.82rem', backgroundColor: '#fff' },
  overlayBtn: { backgroundColor: '#1B2A4A', color: '#fff', textTransform: 'none', fontSize: '0.78rem', padding: '6px 14px', borderRadius: 6, '&:hover': { backgroundColor: '#0F1B33' } },
  saveBtn: { backgroundColor: '#E8772E', color: '#fff', textTransform: 'none', fontSize: '0.78rem', padding: '6px 16px', borderRadius: 6, '&:hover': { backgroundColor: '#Cf6321' } },
  chartCard: { display: 'flex', gap: 16, border: '1px solid #E2E2E2', borderRadius: 10, margin: '4px 24px 20px', padding: 16 },
  chartMain: { flex: 1, minWidth: 0 },
  chartToolbar: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  axisGroup: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  iconBtn: { color: '#555', padding: 6 },
  commentsCol: { width: 200, flexShrink: 0, borderLeft: '1px solid #EEE', paddingLeft: 16 },
  commentsTitle: { fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 },
  noComments: { color: '#999', fontSize: '0.82rem' },
  legendRow: { display: 'flex', gap: 18, marginTop: 8, flexWrap: 'wrap' },
  toggleBtn: { textTransform: 'none', fontSize: '0.72rem', minWidth: 0, padding: '2px 10px', borderColor: '#C7C7C7' },
}));

const LegendSwatch = ({ color, dashed, label, sub }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#444' }}>
    <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '4,3' : '0'} /></svg>
    {label}{sub != null ? ` = ${sub}` : ''}
  </span>
);

const savedViewKey = (batch, proc) => `cppView:${batch}:${proc}`;

// ─── Modal ───────────────────────────────────────────────────────────────────
const TimeSeriesDataModal = ({ open, onClose, batchNumber, initialSeriesKey }) => {
  const classes = useStyles();
  const svgRef = useRef(null);

  const cppKey = resolveCppBatch(batchNumber);
  const data = CPP_DATA[cppKey] || { rows: [], timeSeries: {} };
  const CPP_TIME_SERIES = data.timeSeries;
  // Only rows that actually have a time series are selectable in the chart modal.
  const CPP_ROWS = (data.rows || []).filter((r) => CPP_TIME_SERIES[r.seriesKey]);

  // group rows by procedure number
  const procList = useMemo(() => [...new Set(CPP_ROWS.map((r) => r.procedureNumber))], [CPP_ROWS]);
  const rowsOfProc = (proc) => CPP_ROWS.filter((r) => r.procedureNumber === proc);
  const rowOfKey = (key) => CPP_ROWS.find((r) => r.seriesKey === key);

  const firstKey = initialSeriesKey || (CPP_ROWS[0] && CPP_ROWS[0].seriesKey);
  const firstProc = (rowOfKey(firstKey) || CPP_ROWS[0] || {}).procedureNumber;

  const [proc, setProc] = useState(firstProc);
  const [seriesKey, setSeriesKey] = useState(firstKey);
  const [overlayKey, setOverlayKey] = useState(null);
  const [xAxis, setXAxis] = useState('timestamp');
  const [chartType, setChartType] = useState('line');
  const [savedNote, setSavedNote] = useState('');

  // When opened (or opened from a different row) sync selection + restore any saved overlay for that procedure.
  useEffect(() => {
    if (!open) return;
    const k = initialSeriesKey || firstKey;
    const p = (rowOfKey(k) || {}).procedureNumber || firstProc;
    setProc(p);
    setSeriesKey(k);
    setOverlayKey(loadSavedOverlay(p, k));
    setSavedNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSeriesKey]);

  function loadSavedOverlay(p, primaryKey) {
    try {
      const raw = localStorage.getItem(savedViewKey(cppKey, p));
      if (!raw) return null;
      const v = JSON.parse(raw);
      // only restore if the saved primary matches the current one and overlay still exists
      if (v && v.overlayKey && CPP_TIME_SERIES[v.overlayKey] && v.overlayKey !== primaryKey) return v.overlayKey;
    } catch (e) { /* ignore */ }
    return null;
  }

  const onProc = (p) => {
    setProc(p);
    const first = rowsOfProc(p)[0];
    const pk = first ? first.seriesKey : null;
    setSeriesKey(pk);
    setOverlayKey(loadSavedOverlay(p, pk));
    setSavedNote('');
  };

  const row = rowOfKey(seriesKey) || {};
  const series = CPP_TIME_SERIES[seriesKey] || { points: [], title: '', unit: '' };
  const isVolume = series.xType === 'volume'; // chromatogram (CIEX/AIEX): X axis is Volume (mL)
  const overlayRow = overlayKey ? rowOfKey(overlayKey) : null;
  const overlaySeries = overlayKey ? CPP_TIME_SERIES[overlayKey] : null;

  const spPts = (series.points || []).map((p) => p[3]).filter((v) => v != null);
  const spLabel = spPts.length
    ? (Math.min(...spPts) === Math.max(...spPts) ? `${spPts[0]}` : `${Math.min(...spPts)} – ${Math.max(...spPts)}`)
    : null;

  // params available to overlay = same procedure, excluding the primary
  const overlayOptions = rowsOfProc(proc).filter((r) => r.seriesKey !== seriesKey);

  const saveView = () => {
    try {
      localStorage.setItem(savedViewKey(cppKey, proc), JSON.stringify({ primaryKey: seriesKey, overlayKey }));
      setSavedNote('View saved');
      setTimeout(() => setSavedNote(''), 2500);
    } catch (e) { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth classes={{ paper: classes.paper }}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Time Series Data</Typography>
        <Box className={classes.closeWrap} onClick={onClose}>
          <Typography className={classes.closeLabel}>Close</Typography>
          <CloseIcon style={{ color: '#E53935' }} />
        </Box>
      </Box>

      <DialogContent style={{ paddingBottom: 8 }}>
        {/* Selector row: Batch | Procedure | Parameter | Overlay + Save View */}
        <Box className={classes.selectorRow}>
          <Box>
            <Typography className={classes.fieldLabel}>Batch Number:</Typography>
            <FormControl variant="outlined" size="small">
              <Select className={classes.select} value={cppKey} disabled><MenuItem value={cppKey}>{cppKey}</MenuItem></Select>
            </FormControl>
          </Box>
          <Box>
            <Typography className={classes.fieldLabel}>Procedure Number:</Typography>
            <FormControl variant="outlined" size="small">
              <Select className={classes.select} style={{ minWidth: 260, maxWidth: 260 }} value={proc} onChange={(e) => onProc(e.target.value)}>
                {procList.map((p) => {
                  const up = (getRecipeInfo(cppKey, p) || {}).unitProcedure;
                  return (
                    <MenuItem key={p} value={p}>{up ? `${p} ( ${up} )` : p}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography className={classes.fieldLabel}>Parameter:</Typography>
            <FormControl variant="outlined" size="small">
              <Select className={classes.select} value={seriesKey}
                onChange={(e) => { setSeriesKey(e.target.value); if (e.target.value === overlayKey) setOverlayKey(null); }}>
                {rowsOfProc(proc).map((r) => <MenuItem key={r.seriesKey} value={r.seriesKey}>{r.parameter}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography className={classes.fieldLabel}>Overlay Parameter:</Typography>
            <FormControl variant="outlined" size="small">
              <Select className={classes.select} value={overlayKey || ''} displayEmpty
                onChange={(e) => setOverlayKey(e.target.value || null)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {overlayOptions.map((r) => <MenuItem key={r.seriesKey} value={r.seriesKey}>{r.parameter}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box style={{ display: 'flex', gap: 10, marginLeft: 'auto', alignItems: 'center' }}>
            {savedNote && <Typography style={{ fontSize: '0.72rem', color: '#2E7D32' }}>{savedNote}</Typography>}
            <Button className={classes.overlayBtn} disableElevation
              onClick={() => setOverlayKey(overlayKey ? null : (overlayOptions[0] && overlayOptions[0].seriesKey) || null)}>
              {overlayKey ? 'Remove Overlay' : 'Overlay'}
            </Button>
            <Button className={classes.saveBtn} disableElevation onClick={saveView}>Save View</Button>
          </Box>
        </Box>

        <Box className={classes.chartCard}>
          <Box className={classes.chartMain}>
            <Box className={classes.chartToolbar} style={{ justifyContent: 'flex-end' }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconButton className={classes.iconBtn} size="small" title="Filter by date"><CalendarTodayIcon fontSize="small" /></IconButton>
                <IconButton className={classes.iconBtn} size="small" title="Comments"><ChatBubbleOutlineIcon fontSize="small" /></IconButton>
              </Box>
            </Box>

            <CppTimeSeriesChart series={series} overlay={overlaySeries} xAxis={xAxis} chartType={chartType} svgRef={svgRef} />

            <Box className={classes.legendRow}>
              <LegendSwatch color={PRIMARY_COLOR} label={`${row.parameter} (${row.uom})`} />
              {overlayRow && <LegendSwatch color={OVERLAY_COLOR} label={`${overlayRow.parameter} (${overlayRow.uom})`} />}
              {spLabel != null && <LegendSwatch color="#2E7D32" dashed label="Set Point" sub={spLabel} />}
              {series.rangeLow != null && series.rangeHigh != null && (
                <LegendSwatch color="#D32F2F" dashed label="Range" sub={`${series.rangeLow} – ${series.rangeHigh}`} />
              )}
            </Box>
          </Box>

          <Box className={classes.commentsCol}>
            <Typography className={classes.commentsTitle}>Comments</Typography>
            <Typography className={classes.noComments}>No comments yet</Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TimeSeriesDataModal;
